import React, { useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { MdGroup, MdCommit, MdInsertDriveFile, MdCode } from "react-icons/md";

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MdWarningAmber } from "react-icons/md";

const resolveNameParts = (username, members = []) => {
  const match = members.find(m => m.github_username === username);
  return {
    name: match?.name || 'Unauthorized User',
    handle: username
  };
};

const PRPopupCard = React.memo(function PRMetaCard({ groupMembers, prLink, title, body, commits, filesChanged, codeChanges, collaborators, coAuthors, state, onClose }) {
  const scrollRef = useRef();

  const cleanedBody = useMemo(() => {
    if (typeof body !== 'string') return '';
    return body
      .replace(/(?<!\n)(Co-authored[- ]by:)/g, '\n$1')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/[ \t]+$/gm, '')
      .trim()
      .slice(0, 2000); // Optional truncation
  }, [body]);

  const filteredCoAuthors = useMemo(() =>
    coAuthors?.filter(user => !collaborators.includes(user)), [coAuthors, collaborators]);


  const markdownContent = useMemo(() => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      breaks={true}
      components={{
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          return !inline && match ? (
            <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          ) : (
            <code className={className} {...props}>{children}</code>
          );
        }
      }}
    >
      {cleanedBody}
    </ReactMarkdown>
  ), [cleanedBody]);

  const renderCollaborator = useCallback((username, idx) => {
    const { name, handle } = resolveNameParts(username, groupMembers);
    return (
      <CollaboratorBlock key={idx}>
        <CollaboratorName>{name}</CollaboratorName>
        <CollaboratorHandle href={`https://github.com/${username}`} target="_blank" rel="noopener noreferrer">
          @{handle}
        </CollaboratorHandle>
      </CollaboratorBlock>
    );
  }, [groupMembers]);

  return (
    <ContentWrapper>
      <CloseButton onClick={onClose}>✕</CloseButton>
      <Card $status={state}>
        <TitleRow>
          <Label>PR Title</Label>
          <PRLinkAnchor href={prLink} target="_blank" rel="noopener noreferrer">
            View on GitHub
          </PRLinkAnchor>
        </TitleRow>
        <Header>{title}</Header>

        <Section>
          <Label>PR Description</Label>
          <DescriptionBox ref={scrollRef}>
            {markdownContent}
          </DescriptionBox>
        </Section>

        <Section>
          <Label>PR Metadata</Label>

          <MetaGrid>
            <MetaBox color={{ bg: "#eff6ff", border: "#bfdbfe", text: "#3b82f6", value: "#1d4ed8" }}>
              <IconWrapper><MdCommit className="text-blue-600 mr-3" size={24} /></IconWrapper>
              <MetaText color={{ text: "#3b82f6", value: "#1d4ed8" }}>
                <p>Commits</p>
                <p>{commits}</p>
              </MetaText>
            </MetaBox>

            <MetaBox color={{ bg: "#ecfdf5", border: "#a7f3d0", text: "#10b981", value: "#047857" }}>
              <IconWrapper><MdInsertDriveFile className="text-green-600 mr-3" size={24} /></IconWrapper>
              <MetaText color={{ text: "#10b981", value: "#047857" }}>
                <p>Files Changed</p>
                <p>{filesChanged}</p>
              </MetaText>
            </MetaBox>

            <MetaBox color={{ bg: "#f5f3ff", border: "#ddd6fe", text: "#8b5cf6", value: "#6d28d9" }}>
              <IconWrapper><MdCode className="text-purple-600 mr-3" size={24} /></IconWrapper>
              <MetaText color={{ text: "#8b5cf6", value: "#6d28d9" }}>
                <p>Code Changes</p>
                <CodeChangeLine>
                  <Green>+{codeChanges.additions}</Green>
                  <Divider>/</Divider>
                  <Red>-{codeChanges.deletions}</Red>
                </CodeChangeLine>
              </MetaText>
            </MetaBox>

            <MetaBox color={{ bg: "#fef2f2", border: "#fecaca", text: "#dc2626", value: "#991b1b" }}>
              <IconWrapper><MdWarningAmber size={24} /></IconWrapper>
              <MetaText color={{ text: "#dc2626", value: "#991b1b" }}>
                <p>Status</p>
                {state === "open" ? (
                  <Green>Open</Green>
                ) : (
                  <Red>Closed</Red>
                )}
              </MetaText>
            </MetaBox>

            <MetaBox full color={{ bg: "#fef9c3", border: "#fde68a", text: "#facc15", value: "#ca8a04" }}>
              <IconWrapper><MdGroup className="text-yellow-600 mr-3" size={24} /></IconWrapper>
              <MetaText color={{ text: "#fdb833", value: "#ca8a04" }}>
                <p>
                  Code Contributor{collaborators.length !== 1 ? 's' : ''}
                </p>
                <CollaboratorList>
                  {collaborators.map(renderCollaborator)}
                </CollaboratorList>
              </MetaText>
            </MetaBox>

            {filteredCoAuthors?.length > 0 && (
              <MetaBox full color={{ bg: "#fefce8", border: "#fef08a", text: "#eab308", value: "#a16207" }}>
                <IconWrapper><MdGroup className="text-yellow-600 mr-3" size={24} /></IconWrapper>
                <MetaText color={{ text: "#facc15", value: "#ca8a04" }}>
                  <p>
                    Participant{filteredCoAuthors.length !== 1 ? 's' : ''}
                  </p>
                  <CollaboratorList>
                    {filteredCoAuthors.map(renderCollaborator)}
                  </CollaboratorList>
                </MetaText>
              </MetaBox>
            )}
          </MetaGrid>
        </Section>
      </Card>
    </ContentWrapper>
  );
});



const ContentWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding-top: 15px;
    padding-bottom: 15px;
`;

const Card = styled.div`
  width: 90%;
  background: white;
  padding: 2rem 2rem 0rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  margin: 2rem auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  background-color: "#ffffff"; 
   

  border: 1px solid
    ${({ $status }) =>
    $status === "open"
      ? "#86efac"       // green border
      : $status === "closed"
        ? "#ddd6fe"       // purple border
        : "#e5e7eb"};     // default gray

`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const Header = styled.h1`
 text-align: left;
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: left;
  margin-bottom: 0.25rem;
`;

const PRLinkAnchor = styled.a`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0b99ff;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const Label = styled.small`
   text-align: left;
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280; /* Tailwind gray-500 */
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DescriptionBox = styled.div`
 text-align: left;
  max-height: 200px;
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 1rem;
  line-height: 1.625;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overscroll-behavior: contain;
  overflow-y: auto;

    /* Add spacing for markdown elements */
  p, ul, ol, pre, blockquote, table {
    margin-bottom: 1em;
  }

  code {
    background-color: #f3f4f6;
    padding: 0.2em 0.4em;
    border-radius: 4px;
  }

  pre {
    padding: 1em;
    background-color: #111827;
    color: #f3f4f6;
    border-radius: 6px;
    overflow: auto;
  }

  li {
    margin-left: 1.5em;
  }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'full',
})`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
  grid-column: ${({ full }) => (full ? "span 2" : "auto")};
`;

const MetaText = styled.div`
   text-align: left;
  p:first-child {
    font-size: 0.875rem;
    color: ${({ color }) => color.text};
    font-weight: 500;
  }
  p:last-child {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const CollaboratorList = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CollaboratorBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const CollaboratorName = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937; /* Tailwind gray-800 */
`;

const CollaboratorHandle = styled.a`
  font-size: 0.875rem;
  color: #2563eb; /* Tailwind blue-600 */
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CodeChangeLine = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Green = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #16a34a;
`;

const Red = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc2626;
`;

const Divider = styled.span`
  color: #6d28d9; /* match purple if needed */
  padding: 0 0.2rem; /* adjust spacing */
  font-weight: 700;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -8px;     /* Push above the card */
  right: 0;      
  background: transparent;
  border: none;
  font-size: 1.8rem;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #111827;
  }
`;

export default PRPopupCard;
